import { useEffect, useRef } from 'react';
import { useGestureStore, useHandStore, type HandId } from '@store/index';
import { liveBend } from '@engine/gesture';
import { GESTURE } from '@config/constants';
import './GestureHUD.css';

const MODE_LABEL: Record<string, string> = {
  idle: '—',
  pluck: 'PLUCK',
  bend: 'BEND',
  mute: 'MUTE',
};

/**
 * Bend readout — mounted only while `mode === 'bend'`. Reads `liveBend[hand]`
 * in its own rAF loop and writes directly to DOM refs, so the 30 fps pitch
 * stream never causes a React re-render.
 */
function BendReadout({ hand }: { hand: HandId }) {
  const fillRef = useRef<HTMLDivElement>(null);
  const centsRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    let raf = 0;
    let lastCents = NaN;
    const tick = () => {
      const cents = liveBend[hand];
      if (cents !== lastCents) {
        lastCents = cents;
        const pct = Math.max(-1, Math.min(1, cents / GESTURE.BEND_MAX_CENTS));
        const fill = fillRef.current;
        const label = centsRef.current;
        if (fill) {
          fill.style.width = `${Math.abs(pct) * 50}%`;
          fill.style.left = pct >= 0 ? '50%' : `${50 - Math.abs(pct) * 50}%`;
        }
        if (label) {
          label.textContent = `${cents >= 0 ? '+' : ''}${cents.toFixed(0)}¢`;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [hand]);

  return (
    <div className="hud-badge__bend">
      <div className="hud-badge__bend-track">
        <div ref={fillRef} className="hud-badge__bend-fill" />
        <div className="hud-badge__bend-center" />
      </div>
      <span ref={centsRef} className="hud-badge__bend-cents">0¢</span>
    </div>
  );
}

function HandBadge({ hand }: { hand: HandId }) {
  const present = useHandStore((s) => s[hand].present);
  const mode = useGestureStore((s) => s[hand].mode);

  return (
    <div className={`hud-badge hud-badge--${hand}${present ? '' : ' hud-badge--dim'}`}>
      <div className="hud-badge__title">
        <span className="hud-badge__dot" />
        {hand.toUpperCase()}
      </div>
      <div className="hud-badge__mode">{MODE_LABEL[mode]}</div>
      {mode === 'bend' && <BendReadout hand={hand} />}
    </div>
  );
}

export function GestureHUD() {
  return (
    <div className="hud">
      <HandBadge hand="left" />
      <HandBadge hand="right" />
    </div>
  );
}
